import React from "react";
import ReactDOM from "react-dom";

export interface RestoreFocusAfterUpdateProps {
    readonly isUpdating: boolean;
    readonly children: React.ReactNode;
}

// Keeps track of the most recently started update that recorded focus so older updates don't try to set focus
let mostRecentUpdate: RestoreFocusAfterUpdate | undefined;

/**
 * Class component required to support getSnapshotBeforeUpdate and ReactDOM.findDOMNode.
 * see https://reactjs.org/docs/hooks-faq.html#do-hooks-cover-all-use-cases-for-classes and https://reactjs.org/docs/react-dom.html#finddomnode
 *
 * please also modify RestoreFocusAfterUpdate in packages/core-utilities/src/RestoreFocusAfterUpdate.tsx
 * when you modify this function. Scenario: https://microsoft.visualstudio.com/OS/_workitems/edit/41542542
 */
// tslint:disable-next-line: no-class
export class RestoreFocusAfterUpdate extends React.Component<RestoreFocusAfterUpdateProps> {
    private savedFocusElement: HTMLElement | undefined; // tslint:disable-line: readonly-keyword

    public getSnapshotBeforeUpdate(prevProps: RestoreFocusAfterUpdateProps) {
        if (!prevProps.isUpdating && this.props.isUpdating && document.activeElement) {
            const root = ReactDOM.findDOMNode(this);
            if (root && root.contains(document.activeElement)) {
                // Record the focused item if it's part of the current element
                this.savedFocusElement = document.activeElement as HTMLElement;
                mostRecentUpdate = this;
            }
        }
        return null;
    }

    public componentDidUpdate(prevProps: RestoreFocusAfterUpdateProps) {
        // Restore the previously focused item if focus hasn't been set elsewhere
        if (prevProps.isUpdating && !this.props.isUpdating && this.savedFocusElement) {
            const root = ReactDOM.findDOMNode(this) as HTMLElement;
            if (root && document.activeElement === document.body && mostRecentUpdate === this) {
                if (root.contains(this.savedFocusElement)) {
                    this.savedFocusElement.focus();
                } else if (this.savedFocusElement.dataset.biId) {
                    // Look for a new element with the same tag name and data-bi-id as the originally focused element
                    if (root.tagName === this.savedFocusElement.tagName && root.dataset.biId === this.savedFocusElement.dataset.biId) {
                        root.focus();
                    } else {
                        const matchingElement = root.querySelector(`${this.savedFocusElement.tagName}[data-bi-id="${this.savedFocusElement.dataset.biId}"]`);
                        if (matchingElement) {
                            (matchingElement as HTMLElement).focus();
                        }
                    }
                }
            }
            this.savedFocusElement = undefined;
        }
    }

    public render() {
        return this.props.children;
    }
}
